import React, { useState, useEffect, useCallback } from 'react';
import { Sidebar } from './Dashboard';
import { API } from '../App';

const styles = {
  container: { minHeight: '100vh', backgroundColor: '#0f0f1a', display: 'flex' },
  main: { flex: 1, padding: '40px', overflowY: 'auto' },
  pageTitle: { color: '#ffffff', fontSize: '28px', fontWeight: 'bold', marginBottom: '8px' },
  pageSubtitle: { color: '#a0a0b0', fontSize: '14px', marginBottom: '32px' },
  card: { backgroundColor: '#16213e', borderRadius: '16px', padding: '24px', marginBottom: '24px' },
  cardTitle: { color: '#ffffff', fontSize: '16px', fontWeight: 'bold', marginBottom: '20px' },
  row: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' },
  label: { color: '#a0a0b0', fontSize: '13px' },
  value: { color: '#ffffff', fontSize: '14px', fontWeight: 'bold' },
  badge: { padding: '4px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: 'bold' },
  badgeGreen: { backgroundColor: '#0a3a0a', color: '#00cc00' },
  badgeBlue: { backgroundColor: '#0a2a3a', color: '#4a9eff' },
  badgeRed: { backgroundColor: '#3a0a0a', color: '#e94560' },
  badgeAmber: { backgroundColor: '#3a2a0a', color: '#ffaa00' },
  meterTrack: { width: '100%', height: '10px', backgroundColor: '#0f3460', borderRadius: '6px', overflow: 'hidden', marginTop: '8px' },
  meterFill: h => ({ height: '100%', width: `${h}%`, backgroundColor: h >= 90 ? '#e94560' : '#4a9eff', transition: 'width 0.3s' }),
  banner: { borderRadius: '12px', padding: '16px 20px', marginBottom: '24px', fontSize: '14px', lineHeight: '1.5' },
  bannerWarn: { backgroundColor: '#2a1a0a', border: '1px solid #ffaa00', color: '#ffcc66' },
  bannerInfo: { backgroundColor: '#0a1a2a', border: '1px solid #4a9eff', color: '#9ecbff' },
  bannerError: { backgroundColor: '#2a0a0a', border: '1px solid #e94560', color: '#ff8095' },
  toggle: { display: 'inline-flex', backgroundColor: '#0f3460', borderRadius: '10px', padding: '4px', marginBottom: '24px' },
  toggleBtn: active => ({
    padding: '8px 18px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold',
    backgroundColor: active ? '#e94560' : 'transparent', color: active ? '#ffffff' : '#a0a0b0',
  }),
  planGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '20px' },
  planCard: current => ({
    backgroundColor: '#16213e', borderRadius: '16px', padding: '24px',
    border: current ? '2px solid #e94560' : '2px solid #0f3460',
  }),
  planName: { color: '#ffffff', fontSize: '18px', fontWeight: 'bold', marginBottom: '4px' },
  planCap: { color: '#a0a0b0', fontSize: '13px', marginBottom: '16px' },
  planPrice: { color: '#e94560', fontSize: '30px', fontWeight: 'bold' },
  planPer: { color: '#a0a0b0', fontSize: '13px', marginBottom: '20px' },
  primaryBtn: {
    width: '100%', padding: '12px', backgroundColor: '#e94560', border: 'none', borderRadius: '10px',
    color: '#ffffff', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer',
  },
  secondaryBtn: {
    width: '100%', padding: '12px', backgroundColor: 'transparent', border: '1px solid #0f3460', borderRadius: '10px',
    color: '#a0a0b0', fontSize: '14px', cursor: 'pointer',
  },
  manageBtn: {
    padding: '12px 24px', backgroundColor: '#e94560', border: 'none', borderRadius: '10px',
    color: '#ffffff', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer',
  },
  currentTag: { color: '#e94560', fontSize: '12px', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '1px' },
  note: { color: '#a0a0b0', fontSize: '13px', marginTop: '16px', lineHeight: '1.5' },
};

const STATUS_BADGE = {
  active: styles.badgeGreen,
  trialing: styles.badgeBlue,
  past_due: styles.badgeAmber,
  unpaid: styles.badgeRed,
  canceled: styles.badgeRed,
};

function daysUntil(dateStr) {
  if (!dateStr) return null;
  const ms = new Date(dateStr).getTime() - Date.now();
  return Math.max(0, Math.ceil(ms / 86400000));
}

function fmtDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

function Billing() {
  const token = localStorage.getItem('admin_token');
  const [status, setStatus] = useState(null);
  const [interval, setIntervalChoice] = useState('month');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');

  const params = new URLSearchParams(window.location.search);
  const checkoutResult = params.get('checkout');

  const loadStatus = useCallback(async () => {
    try {
      const res = await fetch(`${API}/billing/status`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.status === 401) { window.location.href = '/login'; return; }
      if (!res.ok) { setError('Could not load billing status.'); return; }
      const data = await res.json();
      setStatus(data);
    } catch {
      setError('Could not load billing status.');
    }
  }, [token]);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  async function startCheckout(plan) {
    setBusy(plan);
    setError('');
    try {
      const res = await fetch(`${API}/billing/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ plan, interval }),
      });
      const data = await res.json();
      if (res.ok && data.url) { window.location.href = data.url; return; }
      setError(typeof data.detail === 'string' ? data.detail : 'Could not start checkout.');
    } catch {
      setError('Could not connect to server.');
    }
    setBusy('');
  }

  async function openPortal() {
    setBusy('portal');
    setError('');
    try {
      const res = await fetch(`${API}/billing/portal`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok && data.url) { window.location.href = data.url; return; }
      setError(typeof data.detail === 'string' ? data.detail : 'Could not open billing portal.');
    } catch {
      setError('Could not connect to server.');
    }
    setBusy('');
  }

  if (!status) {
    return (
      <div style={styles.container}>
        <Sidebar active="Billing" />
        <div style={styles.main}>
          <div style={styles.pageTitle}>Billing</div>
          {error
            ? <div style={{ ...styles.banner, ...styles.bannerError }}>{error}</div>
            : <div style={styles.pageSubtitle}>Loading…</div>}
        </div>
      </div>
    );
  }

  const { plan, billing_mode, subscription_status, access_state, usage, catalog } = status;
  const isComp = billing_mode === 'comp';
  const hasSubscription = !!subscription_status;
  const usagePct = usage && usage.cap ? Math.min(100, Math.round((usage.current / usage.cap) * 100)) : 0;
  const trialDays = subscription_status === 'trialing' ? daysUntil(status.trial_end_at) : null;

  return (
    <div style={styles.container}>
      <Sidebar active="Billing" />
      <div style={styles.main}>
        <div style={styles.pageTitle}>Billing</div>
        <div style={styles.pageSubtitle}>Manage your EZ Pace subscription and payment.</div>

        {checkoutResult === 'success' && (
          <div style={{ ...styles.banner, ...styles.bannerInfo }}>
            Thanks! Your subscription is being set up — details below will update momentarily.
          </div>
        )}
        {error && <div style={{ ...styles.banner, ...styles.bannerError }}>{error}</div>}

        {/* Access-state banners */}
        {access_state === 'read_only' && hasSubscription && (
          <div style={{ ...styles.banner, ...styles.bannerWarn }}>
            <strong>Read-only mode.</strong> Your subscription has lapsed, so new sessions and changes are paused.
            Your data is preserved. Update your payment below to restore full access.
          </div>
        )}
        {access_state === 'read_only' && !hasSubscription && (
          <div style={{ ...styles.banner, ...styles.bannerInfo }}>
            <strong>Choose a plan to get started.</strong> Pick a plan below to begin your 30-day free trial.
            A card is required up front, but you won't be charged until the trial ends — cancel anytime before then.
          </div>
        )}
        {access_state === 'locked' && (
          <div style={{ ...styles.banner, ...styles.bannerError }}>
            <strong>Account locked.</strong> This account has been inactive past the read-only period.
            Update your payment to reactivate, or contact support.
          </div>
        )}

        {/* Current subscription summary */}
        {(hasSubscription || isComp) && (
          <div style={styles.card}>
            <div style={styles.cardTitle}>Current Plan</div>
            <div style={styles.row}>
              <span style={styles.label}>Plan</span>
              <span style={styles.value}>{plan ? plan.charAt(0).toUpperCase() + plan.slice(1) : '—'}</span>
            </div>
            {!isComp && (
              <>
                <div style={styles.row}>
                  <span style={styles.label}>Status</span>
                  <span style={{ ...styles.badge, ...(STATUS_BADGE[subscription_status] || styles.badgeBlue) }}>
                    {subscription_status}
                  </span>
                </div>
                <div style={styles.row}>
                  <span style={styles.label}>Billing</span>
                  <span style={styles.value}>{status.interval === 'year' ? 'Annual' : 'Monthly'}</span>
                </div>
                {trialDays !== null && (
                  <div style={styles.row}>
                    <span style={styles.label}>Free trial</span>
                    <span style={styles.value}>{trialDays} days left (renews {fmtDate(status.trial_end_at)})</span>
                  </div>
                )}
                {status.current_period_end_at && subscription_status !== 'trialing' && (
                  <div style={styles.row}>
                    <span style={styles.label}>Next renewal</span>
                    <span style={styles.value}>{fmtDate(status.current_period_end_at)}</span>
                  </div>
                )}
              </>
            )}
            {isComp && (
              <div style={styles.note}>
                Your account is on a complimentary plan managed by EZ Pace — no payment required.
              </div>
            )}
            {!isComp && (
              <div style={{ marginTop: '20px' }}>
                <button style={styles.manageBtn} onClick={openPortal} disabled={busy === 'portal'}>
                  {busy === 'portal' ? 'Opening…' : 'Manage Payment & Plan'}
                </button>
                <div style={styles.note}>
                  Update your card, switch plans, or cancel in the secure Stripe portal.
                </div>
              </div>
            )}
          </div>
        )}

        {/* Usage meter */}
        {usage && (
          <div style={styles.card}>
            <div style={styles.cardTitle}>Usage</div>
            <div style={styles.row}>
              <span style={styles.label}>Dealers + Observers</span>
              <span style={styles.value}>
                {usage.current}{usage.cap ? ` / ${usage.cap}` : ' (unlimited)'}
              </span>
            </div>
            {usage.cap && (
              <>
                <div style={styles.meterTrack}><div style={styles.meterFill(usagePct)} /></div>
                {usagePct >= 90 && (
                  <div style={{ ...styles.note, color: '#ffcc66' }}>
                    You're approaching your plan limit. Upgrade below to add more.
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Plan picker — shown for new tenants without a subscription */}
        {!hasSubscription && !isComp && (
          <>
            <div style={styles.cardTitle}>Choose a Plan</div>
            <div style={styles.toggle}>
              <button style={styles.toggleBtn(interval === 'month')} onClick={() => setIntervalChoice('month')}>Monthly</button>
              <button style={styles.toggleBtn(interval === 'year')} onClick={() => setIntervalChoice('year')}>Yearly · save 10%</button>
            </div>
            <div style={styles.planGrid}>
              {(catalog || []).map(p => {
                const price = interval === 'year' ? p.yearly : p.monthly;
                return (
                  <div key={p.plan} style={styles.planCard(false)}>
                    <div style={styles.planName}>{p.name}</div>
                    <div style={styles.planCap}>{p.cap ? `Up to ${p.cap} dealers + observers` : 'Unlimited dealers + observers'}</div>
                    <div style={styles.planPrice}>${price.toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
                    <div style={styles.planPer}>per {interval === 'year' ? 'year' : 'month'}</div>
                    <button style={styles.primaryBtn} onClick={() => startCheckout(p.plan)} disabled={busy === p.plan}>
                      {busy === p.plan ? 'Starting…' : 'Start 30-Day Free Trial'}
                    </button>
                  </div>
                );
              })}
            </div>
            <div style={styles.note}>
              Prefer to be invoiced instead of paying by card? Contact your EZ Pace account rep to enable manual billing.
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default Billing;
